import { ApiException, CreateUserRequest, DefaultRoles } from "@api/apiClient";
import { useAdminRoles, useCreateUser, useUsers } from "@api/users";
import i18n from "@i18n/i18n";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import AdminsGrid from "./AdminsGrid";

const AdminsPage = observer(() => {
  const [isUserAdding, setIsUserAdding] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [roleError, setRoleError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [isDuplicate, setIsDuplicate] = useState(false);
  const createUser = useCreateUser();
  const { data: adminRoles = [], isLoading: isLoadingRoles } = useAdminRoles();
  const { data: existingUsers = [] } = useUsers();

  // Check for duplicate entry whenever email or role changes
  useEffect(() => {
    if (emailInput && selectedRole) {
      const duplicateExists = existingUsers.some((user) => user.email?.toLowerCase() === emailInput.toLowerCase() && user.roles?.includes(selectedRole));
      setIsDuplicate(duplicateExists);
    } else {
      setIsDuplicate(false);
    }
  }, [emailInput, selectedRole, existingUsers]);

  const handleBlur = () => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailInput && !regex.test(emailInput)) {
      setEmailError("Invalid email format.");
    } else {
      setEmailError("");
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;

    if (!selectedRole) {
      setRoleError("Role is required.");
      isValid = false;
    } else {
      setRoleError("");
    }

    if (!emailInput) {
      setEmailError("Email is required.");
      isValid = false;
    }

    return isValid && !emailError;
  };

  const handleAdduser = async () => {
    if (!validateForm()) {
      return;
    }

    setIsUserAdding(true);

    try {
      const createUserRequest = new CreateUserRequest({
        email: emailInput,
        role: selectedRole as DefaultRoles,
      });
      await createUser.mutateAsync(createUserRequest);

      setSnackbarMessage(i18n.t("admin-tab.page.add-success-message"));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setEmailInput("");
      setSelectedRole("");
    } catch (error: unknown) {
      // Check if it's a 409 Conflict (duplicate entry)
      if (error instanceof ApiException && error.status === 409) {
        setSnackbarMessage(i18n.t("admin-tab.page.duplicate-entry-error"));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else {
        // Handle other errors
        setSnackbarMessage(i18n.t("admin-tab.page.add-error-message"));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } finally {
      setIsUserAdding(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Container maxWidth={false} sx={{ height: "100%" }}>
      <Grid mt={2} container spacing={2} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Grid size={12} sx={{ display: "flex", flexGrow: 0 }}>
          <Typography variant="h6" fontWeight="bold">
            {i18n.t("admin-tab.page.title")}
          </Typography>
        </Grid>
        <Grid size={12} sx={{ display: "flex", flexGrow: 0, width: "100%", position: "relative" }}>
          <Card elevation={3} sx={{ width: "100%", "&.MuiPaper-root": { transition: "none important!" } }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "1", width: "100%" }}>
                <Grid container spacing={2}>
                  <Grid size={4}>
                    <TextField
                      fullWidth
                      label={i18n.t("admin-tab.page.email-input-text")}
                      error={!!emailError}
                      helperText={emailError}
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onBlur={handleBlur}
                      required
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormControl fullWidth error={!!roleError} required>
                      <InputLabel id="role-select-label">{i18n.t("admin-tab.page.role-select-label")}</InputLabel>
                      <Select
                        labelId="role-select-label"
                        id="role-select"
                        value={selectedRole}
                        label={i18n.t("admin-tab.page.role-select-label")}
                        onChange={(e) => {
                          setSelectedRole(e.target.value);
                          setRoleError("");
                        }}
                        disabled={isLoadingRoles}
                      >
                        {adminRoles.map((role) => (
                          <MenuItem key={role.value} value={role.value}>
                            {role.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {roleError && <FormHelperText>{roleError}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid size={1}>
                    <Tooltip title={isDuplicate ? i18n.t("admin-tab.page.duplicate-entry-error") : i18n.t("admin-tab.page.add-button-tooltip")}>
                      <span>
                        <Button loading={isUserAdding} variant="outlined" sx={{ verticalAlign: "center", paddingTop: "10px" }} onClick={handleAdduser} disabled={isDuplicate}>
                          {i18n.t("admin-tab.page.add-button-text")}
                        </Button>
                      </span>
                    </Tooltip>
                  </Grid>
                  {isDuplicate && (
                    <Grid size={12}>
                      <Typography variant="caption" color="error">
                        {i18n.t("admin-tab.page.duplicate-entry-error")}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={12} sx={{ display: "flex", flexGrow: 1, width: "100%", position: "relative" }}>
          <AdminsGrid />
        </Grid>
      </Grid>
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
});

export default AdminsPage;
